import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Toaster } from 'react-hot-toast';
import { store } from './store/store';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute.jsx';
import LoadingScreen from './components/common/LoadingScreen.jsx';
import ErrorBoundary from './components/common/ErrorBoundary.jsx';

// Helper to retry dynamic imports when a chunk load error occurs (transient dev/HMR issues)
const lazyWithRetry = (importFn, attempts = 3, delay = 1000) => {
  return lazy(() => new Promise((resolve, reject) => {
    const run = (triesLeft) => {
      importFn()
        .then(resolve)
        .catch((err) => {
          const msg = err?.message || '';
          const isChunkError = err?.name === 'ChunkLoadError' || msg.includes('Loading chunk');
          if (triesLeft > 0 && isChunkError) {
            // wait then retry
            setTimeout(() => run(triesLeft - 1), delay);
          } else {
            reject(err);
          }
        });
    };

    run(attempts);
  }));
};

// Lazy load components for better performance (with retry on chunk errors)
const UserRoutes = lazyWithRetry(() => import('./routes/UserRoutes.jsx').then(module => ({ default: module.default })));
const PartnerRoutes = lazyWithRetry(() => import('./routes/PartnerRoutes.jsx').then(module => ({ default: module.default })));
const PartnerInfo = lazyWithRetry(() => import('./pages/Partner/PartnerInfo/PartnerInfo.jsx').then(module => ({ default: module.default })));
const PartnerPending = lazyWithRetry(() => import('./pages/Partner/PartnerPending.jsx').then(module => ({ default: module.default })));
const Products = lazyWithRetry(() => import('./pages/User/Products/Products.jsx').then(module => ({ default: module.default })));
const About = lazyWithRetry(() => import('./pages/About/About.jsx').then(module => ({ default: module.default })));
const Contact = lazyWithRetry(() => import('./pages/Contact/Contact.jsx').then(module => ({ default: module.default })));
const AdminRoutes = lazyWithRetry(() => import('./routes/AdminRoutes.jsx').then(module => ({ default: module.default })));
const TechnicianRoutes = lazyWithRetry(() => import('./routes/TechnicianRoutes.jsx').then(module => ({ default: module.default })));
const Auth = lazyWithRetry(() => import('./pages/Auth/Auth.js').then(module => ({ default: module.default })));

const Landing = lazyWithRetry(() => import('./pages/Landing/Landing.jsx').then(module => ({ default: module.default })));

// TLC Solar Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#FF6B35', // Solar Orange
      light: '#FF8A65',
      dark: '#E64100',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#4CAF50', // Solar Green
      light: '#81C784',
      dark: '#388E3C',
      contrastText: '#FFFFFF',
    },
    tertiary: {
      main: '#FFC107', // Solar Yellow
      light: '#FFD54F',
      dark: '#F57F17',
      contrastText: '#000000',
    },
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2C3E50',
      secondary: '#7F8C8D',
    },
    success: {
      main: '#27AE60',
    },
    warning: {
      main: '#F39C12',
    },
    error: {
      main: '#E74C3C',
    },
    info: {
      main: '#3498DB',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      'Roboto',
      'Helvetica Neue',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 500,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '0.875rem',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '12px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #FF6B35 0%, #F39C12 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #E64100 0%, #E67E22 100%)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          '&:hover': {
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)',
            transition: 'all 0.3s ease',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          color: '#2C3E50',
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <div className="App">
              <ErrorBoundary>
                <Suspense fallback={<LoadingScreen />}>
                  <Routes>
                  {/* Landing Page */}
                  <Route path="/" element={<Landing />} />
                  
                  {/* Authentication */}
                  <Route path="/auth/*" element={<Auth />} />
                  
                  {/* Authentication */}
                  

                    {/* Public Partner Registration (open to all) */}
                    <Route path="/partner-info" element={<PartnerInfo />} />
                      <Route path="/partner/application-submitted" element={<PartnerPending />} />
                  {/* Public pages */}
                  <Route path="/products" element={<Products />} />
                  <Route path="/partners" element={<PartnerInfo />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  
                  {/* User/Buyer Routes */}
                  <Route 
                    path="/app/*" 
                    element={
                      <ProtectedRoute allowedRoles={['user']}>
                        <UserRoutes />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Partner Routes */}
                  <Route 
                    path="/partner/*" 
                    element={
                      <ProtectedRoute allowedRoles={['partner']}>
                        <PartnerRoutes />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Admin Routes */}
                  <Route 
                    path="/admin/*" 
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminRoutes />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Field Technician Routes */}
                  <Route 
                    path="/technician/*" 
                    element={
                      <ProtectedRoute allowedRoles={['technician']}>
                        <TechnicianRoutes />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Redirect unknown routes */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
              
              {/* Global Toast Notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                    borderRadius: '8px',
                  },
                  success: {
                    iconTheme: {
                      primary: '#4CAF50',
                      secondary: '#FFFAEE',
                    },
                  },
                  error: {
                    iconTheme: {
                      primary: '#E74C3C',
                      secondary: '#FFFAEE',
                    },
                  },
                }}
              />
            </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;